import type { QuestionSummary } from '@compass/client/types/analytics';
import React from 'react';

import { Skeleton } from '@/components/ui/skeleton';
import { formatPercent1, formatPotentialPp } from '@/pages/analytics/utils/questionDeepDive';

import { ChartCard } from './ChartCard';
import { InfoTooltip } from './InfoTooltip';

interface QuestionScoreImpactProps {
  summary?: QuestionSummary;
  loading?: boolean;
}

const StatCell: React.FC<{
  label: React.ReactNode;
  value: React.ReactNode;
  sub?: React.ReactNode;
  valueClassName?: string;
}> = ({ label, value, sub, valueClassName }) => (
  <div className="flex-1 rounded-lg border bg-card p-4">
    <div className="flex items-center gap-1 text-sm text-muted-foreground">{label}</div>
    <p
      className={`mt-1 text-2xl font-semibold tabular-nums ${valueClassName ?? 'text-foreground'}`}
    >
      {value}
    </p>
    {sub ? <p className="mt-1 text-xs text-muted-foreground">{sub}</p> : null}
  </div>
);

export const QuestionScoreImpact: React.FC<QuestionScoreImpactProps> = ({ summary, loading }) => {
  return (
    <ChartCard
      title="Score Impact"
      info="How big this miss is across the selected scope — use it to decide whether the question is worth prioritizing for coaching."
      bodyClassName="px-4 pb-4 pt-0"
    >
      {loading || !summary ? (
        <div className="flex flex-col gap-3 sm:flex-row">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex-1 rounded-lg border bg-card p-4">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="mt-2 h-7 w-24" />
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col gap-3 sm:flex-row">
          <StatCell
            label="Miss Rate"
            value={formatPercent1(summary.miss_rate ?? 0)}
            sub={`${summary.missed_count ?? 0} / ${summary.total_count ?? 0} missed`}
            valueClassName="text-destructive"
          />
          <StatCell label="Avg Score on Question" value={formatPercent1(summary.avg_score ?? 0)} />
          <StatCell
            label={
              <>
                Potential for Improvement
                <InfoTooltip
                  content={`If agents stopped missing this question for the selected scope, the average quality score would rise from ${formatPercent1(
                    summary.baseline_score ?? 0,
                  )} to ${formatPercent1(summary.projected_score ?? 0)}.`}
                />
              </>
            }
            value={formatPotentialPp(summary.potential_pp ?? 0)}
            sub={`${formatPercent1(summary.baseline_score ?? 0)} → ${formatPercent1(
              summary.projected_score ?? 0,
            )} (for calls in the selected time period that this question applies to)`}
            valueClassName="text-success"
          />
        </div>
      )}
    </ChartCard>
  );
};
